import {
  Banner,
  Button,
  Icon,
  InputSelectionList,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';

import { customFieldsApi } from 'apis/customFields';
import {
  BaseCustomFieldSelect,
  BaseCustomFieldText,
  errorNotification,
  successNotification,
} from 'components/customFields';
import { useNotifications } from 'hooks/useNotifications';
import {
  CustomField,
  isSelectCustomField,
  isTextCustomField,
  useCustomFields,
} from 'utils/customFields';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './addContactAttributesModal.module.scss';
import { ContactAttributeFormField } from './components/contactAttributeFormField/contactAttributeFormField';
import { getCustomFieldValuePatch } from './utils/getCustomFieldValuePatch';

const formId = 'add-contact-attributes-form';

const searchIntervalMs = 300;

export const AddContactAttributesModal = createAppModal(
  { type: 'contact:addContactAttributes' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const { user } = modal.arg;

    const hasManageCustomAttributesAccess = useHasAccess(
      'settings.custom_attribute',
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [visibleAttributeIds, setVisibleAttributeIds] = useState<
      readonly string[]
    >([]);
    const inputRef = useRef<HTMLDivElement>(null);

    useDebounce(() => setDebouncedSearchTerm(searchTerm), searchIntervalMs, [
      searchTerm,
    ]);
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const { customFields, asyncStatus } = useCustomFields({
      entityId: 'contact',
      entityInstanceId: user.id,
    });

    const { control, formState, handleSubmit, reset, setValue } = useForm();
    const { isSubmitting, isDirty } = formState;

    const [updateValue, { isLoading }] =
      customFieldsApi.useUpdateValueMutation();
    const onSubmit = useCallback(
      async (data: FieldValues) => {
        const patch = getCustomFieldValuePatch({
          customFields,
          formValues: data,
          visibleAttributeIds,
        });
        const changedFields = customFields.filter(
          ({ attribute: { id } }) => patch.before[id] || patch.after[id],
        );

        if (!changedFields.length) {
          hide();
          return;
        }

        changedFields.forEach(({ attribute: { id, type } }) => {
          ampli.customAttributeValueUpdated({
            entityId: 'contact',
            entityInstanceId: user.id,
            attributeId: id,
            type,
          });
        });

        const response = await updateValue({
          patch,
          entityId: 'contact',
          entityInstanceId: user.id,
        });

        if (isResponseWithData(response)) {
          notify(successNotification);
          hide();
        } else {
          notify(errorNotification);
        }
      },
      [customFields, visibleAttributeIds, notify, hide, updateValue, user.id],
    );

    const visibleFields = useMemo(
      () =>
        visibleAttributeIds.flatMap<CustomField>((id) => {
          const customField = customFields.find(
            ({ attribute }) => attribute.id === id,
          );
          return customField ? [customField] : [];
        }),
      [customFields, visibleAttributeIds],
    );

    const availableFields = customFields.filter(
      ({ attribute: { id } }) => !visibleAttributeIds.includes(id),
    );

    const noAttributes = !customFields.length && asyncStatus === 'fulfilled';

    useEffect(() => {
      const savedFields = customFields.filter(({ value }) => value != null);

      setVisibleAttributeIds(savedFields.map(({ attribute }) => attribute.id));
      reset(
        Object.fromEntries(
          savedFields.map(({ attribute, value }) => [
            attribute.id,
            value?.data_type === 'string_list' ? [value.value] : value?.value,
          ]),
        ),
      );
    }, [customFields, reset]);

    // Focus the attribute selector when the modal opens (only if no existing values)
    useEffect(() => {
      const hasExistingValues = customFields.some(({ value }) => value);
      if (isOpen && !hasExistingValues) {
        // Use a small delay to ensure the modal is fully rendered
        const timer = setTimeout(() => {
          inputRef.current?.click();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isOpen, customFields]);

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Edit contact attributes" onClose={hide} />

        <ModalBody>
          {noAttributes ? (
            <Banner
              variant="warning"
              headline="No attributes to edit"
              actionSlot={
                hasManageCustomAttributesAccess && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      hide();
                      navigate({ path: '/settings/custom-attributes/contact' });
                    }}
                  >
                    Add attributes in settings
                  </Button>
                )
              }
              description={
                <Text.Paragraph>
                  There are no active contact attributes in your organization.
                </Text.Paragraph>
              }
            />
          ) : (
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              {visibleFields.map((customField) => {
                const { id } = customField.attribute;
                const onRemove = () => {
                  setValue(id, undefined, { shouldDirty: true });
                  setVisibleAttributeIds((ids) =>
                    ids.filter((visibleId) => visibleId !== id),
                  );
                };

                if (isTextCustomField(customField)) {
                  return (
                    <ContactAttributeFormField key={id} onClick={onRemove}>
                      <BaseCustomFieldText
                        customField={customField}
                        control={control}
                      />
                    </ContactAttributeFormField>
                  );
                }

                if (isSelectCustomField(customField)) {
                  return (
                    <ContactAttributeFormField key={id} onClick={onRemove}>
                      <BaseCustomFieldSelect
                        customField={customField}
                        control={control}
                      />
                    </ContactAttributeFormField>
                  );
                }

                return null;
              })}
              <Tooltip
                className={styles.attributeDropdown}
                content={
                  availableFields.length === 0
                    ? 'No more attributes to add'
                    : ''
                }
              >
                <InputSelectionList
                  ref={inputRef}
                  // Disable dropdown if all empty fields were already added
                  disabled={availableFields.length === 0}
                  placeholder="Select attribute"
                  getItemTitleByValue={(
                    field: CustomField | null | undefined,
                  ) => field?.attribute.label}
                  filter={{
                    name: 'contactFieldSearchQuery',
                    value: searchTerm,
                    onChange: (e) => setSearchTerm(e.target.value),
                  }}
                  onToggleDropdown={(isOpen) => {
                    if (!isOpen) {
                      setSearchTerm('');
                    }
                  }}
                  onChange={(event) => {
                    if (event.selectedItem?.value) {
                      const { attribute } = event.selectedItem.value;
                      setVisibleAttributeIds((ids) => [...ids, attribute.id]);
                    }
                  }}
                  prefix={{
                    type: 'custom',
                    value: <Icon name="input" />,
                  }}
                >
                  {availableFields
                    // Apply search filter
                    .filter(({ attribute: { label } }) =>
                      label
                        .toLowerCase()
                        .includes(debouncedSearchTerm.trim().toLowerCase()),
                    )
                    .map((field) => (
                      <SelectionList.Item
                        key={field.attribute.id}
                        value={field}
                      >
                        <Text.Paragraph>{field.attribute.label}</Text.Paragraph>
                      </SelectionList.Item>
                    ))}
                </InputSelectionList>
              </Tooltip>
            </form>
          )}
        </ModalBody>
        <ModalFoot
          cancelOnClick={hide}
          confirmName="Save"
          confirmOnClick={noop}
          confirmProps={{
            isLoading,
            type: 'submit',
            form: formId,
            disabled: noAttributes || !isDirty || isSubmitting,
            loadingIndicator: 'Saving...',
          }}
        />
      </Modal>
    );
  },
);
